import React, { createContext, useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  documentListItemType,
  LegalDocumentType,
  LegalFormalityType,
} from '../types'
import {
  findDoc,
  isDocPresent,
} from '../pages/Contact/components/LegalFormalities/Panes/Documents'
import { CurrentLegalFormalityContext } from './LegalFormalityContext'
import { MarkdownVersion } from '../types/legalDocument'

const getLatestMarkdown = (doc?: LegalDocumentType): string => {
  const versions = doc?.markdownContent as MarkdownVersion[] | null | undefined
  if (!versions || versions.length === 0) return ''
  return versions[versions.length - 1].content || ''
}

export interface ILegalFormalityDocumentContextValue {
  sortedDocumentList?: documentListItemType[]
  setSortedDocumentList: any
  selectedDocumentListItem?: documentListItemType
  setSelectedDocumentListItem: any
  currentDocument?: LegalDocumentType
  setCurrentDocument: any
  currentMarkdown: string
  setCurrentMarkdown: (markdown: string) => void
}

export interface ILegalFormalityDocumentContextProvider {
  children: any
}
export const LegalFormalityDocumentContext = createContext(
  {} as ILegalFormalityDocumentContextValue
)

const LegalFormalityDocumentContextProvider = (
  props: ILegalFormalityDocumentContextProvider
) => {
  const { t } = useTranslation()
  const { legalFormality } = useContext(CurrentLegalFormalityContext)

  // custom sort of docs : first the mandatory, then presents, then others
  const sortDocList = (legalFormality: LegalFormalityType) => {
    const documents = legalFormality.documents
    const documentList = [...legalFormality.documentList]

    return documentList.sort(
      (a: documentListItemType, b: documentListItemType) => {
        if (a.required && !b.required) {
          return -1
        } else if (!a.required && b.required) {
          return 1
        }

        if (isDocPresent(documents, a) && !isDocPresent(documents, b)) {
          return -1
        } else if (!isDocPresent(documents, a) && isDocPresent(documents, b)) {
          return 1
        }

        if (a.documentType == b.documentType) {
          return 0
        }

        return t(`legalFormalities:document_types.${a.documentType}`) >
          t(`legalFormalities:document_types.${b.documentType}`)
          ? 1
          : -1
      }
    )
  }

  const [sortedDocumentList, setSortedDocumentList] = useState<
    documentListItemType[] | undefined
  >()
  const [selectedDocumentListItem, setSelectedDocumentListItem] = useState<
    documentListItemType | undefined
  >()
  const [currentDocument, setCurrentDocument] = useState<
    LegalDocumentType | undefined
  >()
  const [currentMarkdown, setCurrentMarkdown] = useState<string>('')

  useEffect(() => {
    const docList = sortDocList(legalFormality)
    setSortedDocumentList(docList)
    setSelectedDocumentListItem(
      selectedDocumentListItem || (docList && docList[0])
    )
    setCurrentDocument(
      selectedDocumentListItem &&
        findDoc(legalFormality.documents, selectedDocumentListItem)
    )
  }, [legalFormality])

  useEffect(() => {
    setCurrentMarkdown(getLatestMarkdown(currentDocument))
  }, [currentDocument])

  return (
    <LegalFormalityDocumentContext.Provider
      value={{
        sortedDocumentList,
        setSortedDocumentList,
        selectedDocumentListItem,
        setSelectedDocumentListItem,
        currentDocument,
        setCurrentDocument,
        currentMarkdown,
        setCurrentMarkdown,
      }}
    >
      {props.children}
    </LegalFormalityDocumentContext.Provider>
  )
}

export default LegalFormalityDocumentContextProvider
